import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  HiChevronLeft, 
  HiChevronRight, 
  HiCalendar,
  HiPlus 
} from 'react-icons/hi2';
import { cn } from '../../../utils/formatter';
import type { CalendarView } from '../hooks/useCalendarViews';
import { GlassHoverEffect } from '../../../components/GlassHoverEffect';

interface CalendarToolbarProps {
  view: CalendarView;
  currentDate: Date;
  onViewChange: (view: CalendarView) => void;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onNavigateToday: () => void;
  onAddHoliday?: () => void;
  isAdmin: boolean;
  showDemands: boolean;
  onToggleShowDemands: () => void;
}

const VIEW_LABELS: Record<CalendarView, string> = {
  day: 'Dia',
  week: 'Semana',
  month: 'Mês',
  quarter: 'Trimestre',
  semester: 'Semestre',
  year: 'Ano'
};

const VIEW_OPTIONS: CalendarView[] = ['day', 'week', 'month', 'quarter', 'semester', 'year'];

export const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  view,
  currentDate,
  onViewChange,
  onNavigatePrev,
  onNavigateNext,
  onNavigateToday,
  onAddHoliday,
  isAdmin,
  showDemands,
  onToggleShowDemands
}) => {
  const getDateLabel = () => {
    switch (view) {
      case 'day':
        return format(currentDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
      case 'week':
        return format(currentDate, "'Semana de' dd 'de' MMMM", { locale: ptBR });
      case 'month':
        return format(currentDate, "MMMM 'de' yyyy", { locale: ptBR });
      case 'quarter':
        return format(currentDate, "'Trimestre -' MMMM 'de' yyyy", { locale: ptBR });
      case 'semester':
        return format(currentDate, "'Semestre -' MMMM 'de' yyyy", { locale: ptBR });
      case 'year':
        return format(currentDate, "yyyy", { locale: ptBR });
      default:
        return format(currentDate, "MMMM 'de' yyyy", { locale: ptBR });
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 p-3 mb-3">
      <div className="flex items-center justify-between gap-4">
        {/* Left: Title and Navigation */}
        <div className="flex items-center gap-3">
          <HiCalendar className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <h1 className="text-base font-semibold text-gray-900 dark:text-white capitalize whitespace-nowrap">
            {getDateLabel()}
          </h1>
          
          {/* Navigation */}
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={onNavigatePrev}
              className="p-1.5 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors"
              title="Período anterior"
            >
              <HiChevronLeft className="w-4 h-4" />
            </button>
            
            <button
              onClick={onNavigateToday}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors"
            >
              Hoje
            </button>
            
            <button
              onClick={onNavigateNext}
              className="p-1.5 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors"
              title="Próximo período"
            >
              <HiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right: View Selector and Add Button */}
        <div className="flex items-center gap-3">
          {/* Toggle Demandas */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-neutral-900 rounded-md">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Demandas
            </span>
            <button
              onClick={onToggleShowDemands}
              className={cn(
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                showDemands
                  ? 'bg-blue-500'
                  : 'bg-gray-300 dark:bg-neutral-700'
              )}
              title={showDemands ? 'Ocultar demandas' : 'Mostrar demandas'}
            >
              <span
                className={cn(
                  'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                  showDemands ? 'translate-x-5' : 'translate-x-1'
                )}
              />
            </button>
          </div>

          {/* View Selector with GlassHoverEffect */}
          <GlassHoverEffect
            activeIndex={VIEW_OPTIONS.indexOf(view)}
            direction="horizontal"
          >
            {({ containerRef, containerProps, getItemProps }) => (
              <nav
                ref={containerRef}
                className="bg-gray-50 dark:bg-neutral-900 rounded-md p-0.5 flex gap-0.5"
                {...containerProps}
              >
                {VIEW_OPTIONS.map((viewOption, index) => (
                  <button
                    key={viewOption}
                    {...getItemProps(index)}
                    onClick={() => onViewChange(viewOption)}
                    className={cn(
                      'px-2.5 py-1 rounded text-[11px] font-medium transition-colors duration-200 relative z-10',
                      view === viewOption
                        ? 'text-blue-700 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-neutral-800'
                    )}
                  >
                    {VIEW_LABELS[viewOption]}
                  </button>
                ))}
              </nav>
            )}
          </GlassHoverEffect>

          {/* Add Button */}
          {isAdmin && onAddHoliday && (
            <button
              onClick={onAddHoliday}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors text-xs font-medium flex-shrink-0"
            >
              <HiPlus className="w-3.5 h-3.5" />
              Adicionar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

